import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format, parseISO, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import './PollsTable.css';
import Button from './Button.js';

const PollsTable = ({ 
  data,
  onSendPollClick,
  onClosePollClick,
  onArchivePollClick,
  pollState,
  handleSubmitVotes,
  handleDeleteOption,
  userRole,
  selectedVotes,
  setSelectedVotes,
  handleVoteChange
}) => {

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Sort data by startTime — already a complete UTC ISO string, no reconstruction needed
  const sortedData = data.sort((a, b) => {
    const dateTimeA = parseISO(a.startTime);
    const dateTimeB = parseISO(b.startTime);

    if (!isValid(dateTimeA) || !isValid(dateTimeB)) {
      return 0;
    }

    return dateTimeA - dateTimeB;
  });

  return (
    <div className="polls-table-container">
      <div className="tableText">
        {pollState === 'open' && (
          <p className='sendPollText'>
            <a href="#" onClick={(e) => { e.preventDefault(); onSendPollClick(); }}>
              Send poll <span className="material-icons">email</span>
            </a>
          </p>
        )}
        {userRole === 'commissioner' && pollState === 'shared' && (
          <p className='sendPollText'>
            <a href="#" onClick={(e) => { e.preventDefault(); onClosePollClick(); }}>
              Close poll
            </a>
          </p>
        )}
        {pollState === 'shared' && (
          <p className='sendPollText'>
            <Button label="Submit Votes" onClick={handleSubmitVotes} />
          </p>
        )}
        {userRole === 'commissioner' && pollState === 'closed' && (
          <p className='sendPollText'>
            <a href="#" onClick={(e) => { e.preventDefault(); onArchivePollClick(); }}>
              Archive poll
            </a>
          </p>
        )}
      </div>
      <table className="polls-table">
        <thead>
          <tr>
            <th>Vote</th>
            <th>Date</th>
            <th>Start Time</th>
            <th>Votes</th>
            {/* Show Delete header only if the user is a commissioner and poll is open */}
            {userRole === "commissioner" && pollState === "open" && <th>Delete</th>}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((pollDetail) => {
            try {
              // Ensure pollDetail.date and pollDetail.startTime are valid
              const combinedDateTime = parseISO(pollDetail.startTime);

              if (!isValid(combinedDateTime)) {
                throw new Error("Invalid date or time format.");
              }

              // Convert to user's time zone
              const localDate = formatInTimeZone(combinedDateTime, timeZone, 'MMM-dd-yyyy');
              const localStartTime = formatInTimeZone(combinedDateTime, timeZone, 'hh:mm a');

              return (
                <tr key={pollDetail._id}>
                  <td data-label="Vote">
                    <input
                      type="checkbox"
                      disabled={pollState !== 'shared'}
                      checked={selectedVotes.includes(pollDetail._id)}
                      onChange={() => {
                        handleVoteChange(pollDetail._id);
                      }}
                    />
                  </td>
                  <td data-label="Date">{localDate}</td>
                  <td data-label="Start Time">{localStartTime}</td>
                  <td data-label="Votes">{pollDetail.voteCount}</td>
                  {userRole === "commissioner" && pollState === "open" && (
                    <td data-label="Delete">
                      <button 
                        className="delete-btn" 
                        onClick={() => handleDeleteOption(pollDetail._id)}
                      >
                        🗑️
                      </button>
                    </td>
                  )}
                </tr>
              );
            } catch (error) {
              console.error('Invalid date or time value:', error);
              return (
                <tr key={pollDetail._id}>
                  <td colSpan={userRole === "commissioner" && pollState === "open" ? 5 : 4}
                      style={{ color: 'red', fontStyle: 'italic' }}>
                    Invalid time entry (ID: {pollDetail._id}) — contact your commissioner.
                  </td>
                </tr>
              );
            }
          })}
        </tbody>
      </table>
    </div>
  );
};

PollsTable.propTypes = {
  data: PropTypes.array.isRequired,
  onSendPollClick: PropTypes.func.isRequired,
  onClosePollClick: PropTypes.func.isRequired,
  onArchivePollClick: PropTypes.func.isRequired,
  handleSubmitVotes: PropTypes.func.isRequired,
  handleDeleteOption: PropTypes.func.isRequired,
  pollState: PropTypes.string.isRequired,
  userRole: PropTypes.string.isRequired // Ensure this matches the expected type
};

export default PollsTable;
